@if (loading) {
<spinner-overlay [fullscreen]="true"></spinner-overlay>
}
<app-menu
    [organisation]="organisation"
    [screen]="screen"
    [searchNodes]="searchNodes"
    (menuEvent)="handleFunctionEvent($event)"
    (selectNodeEvent)="findNode($event)"
></app-menu>
<app-analysis-header [organisation]="organisation" view="actions" [canShowFullModel]="true" [fullModel]="fullModel" (fullModelChange)="fullModelChanged($event)"></app-analysis-header>

@if (isHelpOn()) {
<div class="hint">
    Provides analysis of the model data in the organisation. You can switch
    between current state, projects, models and organisation level data. You can
    also compare between projects and/or current state.
</div>
}

<mat-sidenav-container class="drawer-container">
    <mat-sidenav
        #leftdrawer
        mode="side"
        [opened]="true"
        position="start"
        class="analysis-sidebar"
        disableClose="true"
    >
        @if (fullModel) {
        <section class="sidebar-section">
            <h3>Full model</h3>
            <p class="sidebar-note">Every action, information node, system and role in the model, as drawn in LINQ. Double-click a node for its details. "View summary" returns to the process graph.</p>
        </section>
        }
        @if (organisation && !fullModel) {
        <section id="analysis-level" class="sidebar-section">
            <h3>Level of detail</h3>
            <mat-button-toggle-group class="level-toggle" [value]="pendingLevel || level" (change)="setLevel($event.value)" [disabled]="loading" aria-label="Level of detail" data-testid="graph-level">
                @for (l of levels; track l.level) {
                <mat-button-toggle [value]="l.level" [matTooltip]="l.description">{{ l.label }}</mat-button-toggle>
                }
            </mat-button-toggle-group>
            <p class="sidebar-note">{{ levelNote }}</p>
        </section>
        }
        @if (organisation && !fullModel) {
        <section id="analysis-scorecard" class="sidebar-section">
            <h3>Economic scorecard</h3>
            <p class="sidebar-note">Show where value is created and destroyed in the models shown, with prioritised decisions.</p>
            <button
                mat-flat-button
                color="accent"
                (click)="rightdrawer.toggle()"
                [attr.aria-expanded]="rightdrawer.opened"
                data-testid="open-scorecard"
            >
                <mat-icon>{{ rightdrawer.opened ? 'close' : 'insights' }}</mat-icon>{{ rightdrawer.opened ? 'Close scorecard' : 'Open EPM scorecard' }}
            </button>
        </section>
        }
        @if (organisation && fullModel) {
        <section class="sidebar-section">
            <h3>Heatmap</h3>
            <p class="sidebar-note">Colours the model by an attribute and labels each node with its value, as the toolbar's heatmap does.</p>
            <mat-form-field color="accent" subscriptSizing="dynamic" class="dimension-field">
                <mat-label>Attribute</mat-label>
                <mat-select [value]="fullModelField" (selectionChange)="fullModelFieldChanged($event.value)" data-testid="full-model-field">
                    <mat-option [value]="undefined">None</mat-option>
                    @for (field of insightFields; track field) {
                        <mat-option [value]="field">{{ field }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
        </section>
        }
        @if (organisation && !fullModel) {
        <section id="analysis-heatmap" class="sidebar-section">
            <h3>Heatmap</h3>
            @if (level === 'models') {
            <p class="sidebar-note">Heatmaps show at the Main flow and Actions levels.</p>
            } @else {
            <p class="sidebar-note">Colours the graph by a dimension and shows each step's value.</p>
            <mat-form-field color="accent" subscriptSizing="dynamic" class="dimension-field">
                <mat-label>Dimension</mat-label>
                <mat-select
                    [value]="selectedDimension"
                    (selectionChange)="dimensionSelected($event.value)"
                    [disabled]="loading"
                    data-testid="dimension-picker"
                >
                    <mat-option [value]="undefined">None</mat-option>
                    @for (dimension of availableDimensions; track dimension) {
                        <mat-option [value]="dimension">{{ dimension }}</mat-option>
                    }
                </mat-select>
            </mat-form-field>
            }
            @if (heatmapLegend) {
            <div class="heatmap-legend" data-testid="heatmap-legend">
                @if (heatmapLegend.scale.length) {
                <div class="legend-bar" [style.background]="heatmapLegend.gradient"></div>
                <div class="legend-scale">
                    @for (value of heatmapLegend.scale; track $index) {
                    <span>{{ value }}</span>
                    }
                </div>
                }
                <p class="sidebar-note">{{ heatmapLegend.note }}</p>
            </div>
            }
        </section>
        <section id="analysis-simulation" class="sidebar-section">
            <h3>Simulation</h3>
            <p class="sidebar-note">Runs a year of items through the graph, showing where work queues.</p>
            @if (simulating) {
            <button mat-stroked-button color="accent" (click)="stopSimulation()" data-testid="toggle-simulation">
                <mat-icon>stop</mat-icon>Stop simulation
            </button>
            } @else if (simulationStopped) {
            <div class="simulation-actions">
                @if (!simulationFinished) {
                <button mat-stroked-button color="accent" (click)="continueSimulation()" data-testid="continue-simulation">
                    <mat-icon>play_arrow</mat-icon>Continue
                </button>
                }
                <button mat-stroked-button color="accent" (click)="startSimulation()" [disabled]="loading" data-testid="restart-simulation">
                    <mat-icon>replay</mat-icon>Start again
                </button>
                <button mat-stroked-button (click)="clearSimulation()" data-testid="clear-simulation">
                    <mat-icon>clear</mat-icon>Clear
                </button>
                @if (simulationReport && !showFullResults) {
                <button mat-stroked-button (click)="showFullResults = true" data-testid="show-simulation-results">
                    <mat-icon>assessment</mat-icon>Show results
                </button>
                }
            </div>
            } @else {
            <button mat-stroked-button color="accent" (click)="startSimulation()" [disabled]="loading || !canSimulate" data-testid="toggle-simulation">
                <mat-icon>play_arrow</mat-icon>Run simulation
            </button>
            }
            <button mat-stroked-button class="what-if" (click)="openWhatIf()" [disabled]="loading || !organisationModelData" data-testid="open-what-if"
                matTooltip="Change the load or the people on a role, see what the roles need, and simulate it against today">
                <mat-icon>tune</mat-icon>What if…
            </button>
            @if (simulating || simulationStopped) {
            @let progress = simulationProgress;
            <p class="sidebar-note simulation-progress" role="status" data-testid="simulation-progress">
                {{ simulationFinished ? 'Finished the year' : (simulating ? 'Running' : 'Stopped') + ' at week ' + progress.week + ' of ' + progress.weeks + ', day ' + progress.day }}:
                {{ progress.received | number }} items in, {{ progress.completed | number }} outputs produced, {{ progress.queued | number }} waiting at steps.
            </p>
            }
        </section>
        <section id="analysis-models-list" class="sidebar-section">
            <h3>Models in the graph</h3>
            <app-models-list
                [list]="modelsList"
                (modelSelected)="selectModel($event)"
            ></app-models-list>
        </section>
        }
    </mat-sidenav>
    <mat-sidenav-content class="actions-content">
        @if (fullModel) {
        <app-full-model [organisation]="organisation" [scope]="currentScope"></app-full-model>
        } @else if (level === 'models') {
        <!-- the models and their joins, as the home screen's model diagram draws them -->
        <div class="models-page" data-testid="models-diagram">
            <header class="models-toolbar">
                <h1>Actions model</h1>
                <span>{{ levelSubtitle }}</span>
            </header>
            @if (modelsLinqset) {
            <app-graph-component [linqset]="modelsLinqset" [modelType]="MODEL_TYPE.MODELS" [readOnly]="true" [contextMenu]="false" (itemDoubleClickedEvent)="openModelSteps($event)"></app-graph-component>
            }
        </div>
        } @else {
        <app-business-process-graph
            [process]="processDefinition"
            [models]="graphModels"
            [layoutOrientation]="layoutOrientation"
            heading="Actions model"
            [subtitle]="levelSubtitle"
            [itemToolTip]="heatmapToolTip"
            [nodeToolTip]="nodeToolTip"
            (graphBuilt)="onGraphBuilt($event)"
            (nodeDoubleClicked)="openOtherModel($event)"
        ></app-business-process-graph>
        <app-simulation-results
            [report]="simulationReport"
            [full]="showFullResults"
            (stepSelected)="showSimulationStep($event)"
            (closed)="showFullResults = false"
            [canTest]="canTestSuggestions"
            [testDisabledReason]="testDisabledReason"
            (testChanges)="testSuggestions($event)"
            [canRecommend]="canTestSuggestions"
            (recommend)="openRecommendations()"
        ></app-simulation-results>
        }
    </mat-sidenav-content>
    <mat-sidenav
        #rightdrawer
        mode="side"
        [opened]="false"
        position="end"
        class="scorecard-drawer"
        disableClose="false"
    >
        <button
            mat-icon-button
            class="scorecard-close"
            aria-label="Close the scorecard"
            matTooltip="Close the scorecard"
            (click)="rightdrawer.close()"
        >
            <mat-icon>close</mat-icon>
        </button>
        <app-epm-scorecard
            [models]="models"
            [analysisNodes]="shownData.nodes"
            [analysisEdges]="shownData.edges"
            [organisationModelData]="organisationModelData"
            [filter]="filter"
            [active]="rightdrawer.opened"
            [workspaceKey]="workspaceKey"
            [workspaceName]="workspaceName"
            [refreshTrigger]="refreshTrigger"
            [dimensionSummary]="dimensionSummary"
            (stepSelected)="showStep($event)"
            (valuesApplied)="loadGraph()"
        ></app-epm-scorecard>
    </mat-sidenav>
</mat-sidenav-container>
